/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";

type Validator = {
  validate: () => boolean;
  onInvalid: () => void;
};

const FormContext = createContext<
  MutableRefObject<Map<unknown, Validator>> | undefined
>(undefined);

/**
 * Form component that prevents submission until every field is valid.
 */
export const ExperimentalForm = ({
  onSubmit,
  children,
}: {
  /**
   * A callback function that is invoked when the form is submitted and all form fields are valid.
   */
  onSubmit: () => void;
  /**
   * Content of form
   */
  children: ReactNode;
}) => {
  const validators = useRef(new Map<unknown, Validator>());

  return (
    <form
      css={{ display: "contents" }}
      onSubmit={(e) => {
        e.preventDefault();

        const ids = [...validators.current.keys()];
        let isValid = true;

        for (const id of ids) {
          const validator = validators.current.get(id);

          if (!validator) {
            continue;
          }

          const { validate, onInvalid } = validator;

          if (validate()) {
            continue;
          }

          isValid = false;
          flushSync(() => {
            onInvalid();
          });
        }

        if (isValid) {
          onSubmit();
        }
      }}
    >
      <FormContext.Provider value={validators}>{children}</FormContext.Provider>
    </form>
  );
};

/**
 * Registers a field in `ExperimentalForm` and shows error before submit.
 */
export const useSubmitGuard = ({
  validate,
  onInvalid,
}: {
  /**
   * A function that returns `true` if the field is valid.
   */
  validate: () => boolean;
  /**
   * Callback the form calls when the field is invalid.
   */
  onInvalid: () => void;
}) => {
  const [id] = useState({});
  const validatorsRef = useContext(FormContext);

  useEffect(() => {
    if (!validatorsRef) {
      return;
    }

    const validators = validatorsRef.current;

    validators.set(id, { validate, onInvalid });

    return () => {
      validators.delete(id);
    };
  }, [validate, validatorsRef, onInvalid, id]);
};
